import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "./api";

/** Mirror of plan 01-07's MeResponse DTO. */
export type Me = {
  email: string;
  name?: string;
  isAdmin: boolean;

  hasCoachSubscription?: boolean;
  funnelSource?: string;
  firebaseCustomToken?: string;
  hasActiveSubscription?: boolean;
  subscriptionExpiresAt?: string;
  language?: string;
  assessmentResult?: {
    raw: Record<string, number>;
    percentages: Record<string, number>;
    total: number;
  };
};


/**
 * Auth-state primitive. Calls GET /api/me via TanStack Query.
 * - 200 → returns parsed Me
 * - 401 → throws ApiError; useQuery surfaces as `isError: true`
 */
export function useMe() {
  return useQuery<Me, ApiError>({
    queryKey: ["me"],
    queryFn: () => api<Me>("/api/me"),
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 401) return false;
      return failureCount < 1;
    },
    staleTime: 60_000,
  });
}

/**
 * Logout mutation. Calls POST /api/auth/logout, then clears the entire
 * TanStack Query cache so the next sign-in on the same device starts from
 * a clean slate (audit 260512-nmw B-6).
 *
 * Previously only `["me"]` was invalidated, leaving authenticated query
 * caches (`["catalog", ...]`, `["course", slug]`, `["progress", email]`,
 * `adminKeys.*`, `["library", ...]`) keyed by the prior user. On a shared
 * device the next sign-in flashed prior-user data before the new fetch
 * resolved.
 */
export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ loggedOut: true }>("/api/auth/logout", { method: "POST", body: "{}" }),
    onSuccess: () => {
      queryClient.clear();
      // setQueryData after clear() so useMe() flips to anonymous synchronously
      // (clear() resets to "no data yet" which would refetch). Mirrors the
      // pre-fix behavior callers depend on.
      queryClient.setQueryData(["me"], null);
    },
  });
}
